/**
 * NotificationDrawerItem - Individual notification row in the notification drawer
 *
 * Displays notification with title, subtitle, body text, and product chip.
 * Background color varies based on read status.
 */

import { Box, MenuItem, Chip, Typography, Tooltip } from "@mui/material";
import type { Notification } from "@opengov/bnp-core";
import {
  formatFullDateTime,
  formatRelativeTime,
  resolveNotificationProduct,
  useNotificationsMutation,
} from "@opengov/bnp-core";
import { NotificationTypeAvatar } from "./NotificationTypeAvatar";
import { styles } from "./NotificationsDrawer.styles.tsx";
import { resolveDeepLinkUrl } from "./utils";

interface NotificationDrawerItemProps {
  readonly notification: Notification;
  readonly baseUrl: string;
  readonly entityId: string;
}

export function NotificationDrawerItem({ notification, baseUrl, entityId }: NotificationDrawerItemProps) {
  const { markAsRead } = useNotificationsMutation(baseUrl, entityId, notification.id);
  const product = resolveNotificationProduct(notification);
  return (
    <MenuItem
      onClick={() => {
        if (notification.content.deepLinkUrl) {
          if (!notification.read) markAsRead();
          window.open(resolveDeepLinkUrl(notification.content.deepLinkUrl), "_blank", "noopener,noreferrer");
        } else if (!notification.read) {
          markAsRead();
        }
      }}
      disableRipple
      sx={[styles.menuItem, !notification.read && styles.menuItemUnread]}
    >
      <NotificationTypeAvatar notification={notification} />

      <Box sx={styles.contentColumn}>
        <Typography variant="h4" className="notification-title" sx={styles.title}>
          {notification.content.title}
        </Typography>

        {notification.content.body && (
          <Typography variant="body1" color="text.primary" sx={styles.bodyText} data-test="notificationDrawerBody">
            {notification.content.body}
          </Typography>
        )}

        {/* Footer: product chip and relative timestamp share a row */}
        <Box sx={styles.metaRow}>
          <Chip label={product.displayName} size="small" />

          <Tooltip title={formatFullDateTime(notification.createdAt)} arrow enterNextDelay={300}>
            <Typography variant="caption" color="text.secondary">
              {formatRelativeTime(notification.createdAt)}
            </Typography>
          </Tooltip>
        </Box>
      </Box>
    </MenuItem>
  );
}
